import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { compose, branch, renderComponent, lifecycle } from 'recompose';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { Expand } from '@sparefoot/react-core';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import { steps, OMISteps } from 'components/facility/HowItWorks/steps';

import {
	withRouteHandler,
	withSearchHandler,
	withErrorHandler,
} from 'decorators';
import OGFFacilityPage from 'pages/OGFFacilityPage/OGFFacilityPage';
import facilitySelector from 'store/selectors/facility';
import { getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { createBooking } from 'store/modules/bookings';
import { onCreateReview } from 'store/modules/reviews';
import { change as changeFormData } from 'redux-form';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
import { Spinner } from 'components/core/Spinner';
import { H1, H2, H3, Paragraph } from 'components/core/Typography';
import {
	Page,
	Section,
	Container,
	Group,
	Row,
	Column,
} from 'components/core/Page';

import { Amenities } from 'components/facility/Amenities';
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbs';
import { FacilityAddress } from 'components/facility/FacilityAddress';
import { FacilityHours } from 'components/facility/FacilityHours';
import { FacilityRating } from 'components/facility/FacilityRating';
import { FacilityViews } from 'components/facility/FacilityViews';
import { HowItWorks } from 'components/facility/HowItWorks';
import { OpenNow } from 'components/facility/OpenNow';
import { FacilityReviews } from 'components/facility/FacilityReviews';
import { FacilitySchema } from 'components/facility/FacilitySchema';
import { NearbyFacilities } from 'components/facility/NearbyFacilities';
import { FacilityUnitList } from 'components/facility/FacilityUnitList';
import { StickySearch } from 'components/search/StickySearch';
import { ScarcityBanner } from 'components/search/ScarcityBanner';
import { Notification } from 'components/core/Notification';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import './FacilityPage.scss';
import { TrackingClient } from '@sparefoot/tracking-client';

export const enhance = compose(
	connect(
		facilitySelector,
		{
			changeFormData,
			createBooking,
			onCreateReview,
			getFacilityCardPhoneNumber,
		},
		mapDispatchToActions,
	),
	withErrorHandler,
	withRouteHandler((props) => !get(props, 'facility.location.address')),
	withSearchHandler,
	branch(
		({ facility }) => !facility.inNetwork,
		renderComponent(OGFFacilityPage),
	),
	withShowHide('description'),
	withShowHide('amenities'),
	withPageViewTracking(PAGE_TYPES.FACILITY),
	lifecycle({
		componentDidMount() {
			const { facility } = this.props;
			TrackingClient.trackFacilityViewed({
				facility_id: facility.id,
				facility_name: facility.name,
				facility_promo: facility.promo,
				facility_rank: facility.rank,
			});
		},
	}),
);

export function FacilityPage({
	actions,
	amenities,
	amenitiesShowing,
	descriptionShowing,
	onToggleDescription,
	onToggleAmenities,
	facility,
	phone,
	reviews,
	reviewPageNum,
	reviewSource,
	baseUrl,
	storageUnits,
	vehicleUnits,
	nearbyFacilities,
	handleSearch,
	searchLocation,
	loading,
	trackingPixel,
	meta,
	scarcityShow,
}) {
	const { noIndex } = meta;
	const showOMIFeatures = facility.hasOnlineMoveins;
	return (
		<Page
			className="facility-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.FACILITY}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={noIndex}
			noFollow={noIndex}
		>
			<FacilitySchema
				facility={facility}
				phone={phone}
			/>
			{facility.isOpen && <OpenNow />}
			{scarcityShow && <ScarcityBanner />}
			{!facility.isVisible && (
				<Notification permanent>
					This facility has no units available for online booking.
				</Notification>
			)}
			<Section>
				<StickySearch
					onSearch={handleSearch}
					location={searchLocation}
					sticky={false}
				/>
				<Container className="facility-header">
					<FacilityBreadcrumbs
						facility={facility}
						baseUrl={baseUrl}
					/>
					<Group>
						<Paragraph size="mega">
							{showOMIFeatures
								? 'Risk-free Reservation & Online Move-In'
								: 'Make a risk-free reservation'}
						</Paragraph>
						<Paragraph className="subheading">
							Lock in your low rate. There's no risk - easily
							cancel or change your reservation at any time.
						</Paragraph>
					</Group>
					<Group>
						<H1
							size="large"
							className="facility-name"
							itemprop="name"
						>
							{facility.name}
						</H1>
						<Paragraph className="facility-contact">
							<FacilityAddress address={facility.location} />
							{!!phone && (
								<Link
									secondary
									href={`tel:${phone.number}`}
								>
									{phone.formattedNumber}
								</Link>
							)}
						</Paragraph>
						{!!facility.reviews.number && (
							<FacilityRating
								facilityName={facility.name}
								numberOfRatings={facility.reviews.number}
								average={facility.reviews.average}
								reviewsUrl="#facility-page-user-reviews"
							/>
						)}
						{!facility.reviews.number && (
							<Paragraph className="leave-a-review">
								No reviews found.{' '}
								<Link
									secondary
									href="#leave-a-review"
								>
									Be the first to review
								</Link>
							</Paragraph>
						)}
					</Group>
				</Container>
			</Section>
			<Section>
				<Container>
					<Row>
						<Column large={8}>
							{!!facility.description && (
								<Section>
									<H3
										size="medium"
										className="headline"
									>
										About {facility.name}
									</H3>
									<Expand
										showing={descriptionShowing}
										showTeaser
									>
										<div className="facility-description">
											<Paragraph last>
												<span
													// eslint-disable-next-line react/no-danger
													dangerouslySetInnerHTML={{
														__html: facility.description.replace(
															/\n/gi,
															'<br />',
														),
													}}
												/>
											</Paragraph>
										</div>
									</Expand>
									<Button
										link
										secondary
										onClick={onToggleDescription}
										className="facility-learn-more"
									>
										Read{' '}
										{descriptionShowing ? 'less' : 'more'}
									</Button>
								</Section>
							)}
							<FacilityViews facility={facility} />
						</Column>
						<Column large={4}>
							<Expand
								showing={amenitiesShowing}
								showTeaser
							>
								<div className="facility-amenities">
									<Section>
										<H2
											size="medium"
											className="headline"
										>
											Facility Amenities
										</H2>
										{isEmpty(amenities) && loading && (
											<Spinner />
										)}
										{!isEmpty(amenities) && (
											<Amenities amenities={amenities} />
										)}
									</Section>
									{!!facility.hours && (
										<Section>
											<FacilityHours
												accessHours={
													facility.hours.access
												}
												officeHours={
													facility.hours.office
												}
											/>
										</Section>
									)}
								</div>
							</Expand>
							<Button
								link
								secondary
								onClick={onToggleAmenities}
								className="amenities-learn-more"
							>
								Read {amenitiesShowing ? 'less' : 'more'}
							</Button>
						</Column>
					</Row>
				</Container>
			</Section>
			<Section>
				<Container>
					<Row reverse>
						<Column large={4}>
							<H3
								size="medium"
								className="headline"
							>
								How it Works
							</H3>
							<HowItWorks
								steps={showOMIFeatures ? OMISteps : steps}
							/>
						</Column>
						<Column large={8}>
							<Section id="risk-free-message">
								<H3 size="medium">
									Make a risk-free reservation to lock in your
									low rate.
								</H3>
								<Paragraph
									last
									className="risk-free-subtext"
								>
									No payment until move in. Easily cancel or
									change reservation online.
								</Paragraph>
							</Section>
							<Section
								id="units"
								className="facility-units"
							>
								<FacilityUnitList
									facility={facility}
									storageUnits={storageUnits}
									vehicleUnits={vehicleUnits}
									changeFormData={actions.changeFormData}
									onCreateBooking={actions.createBooking}
									loading={loading}
								/>
							</Section>
							<Section id="facility-page-user-reviews">
								<FacilityReviews
									reviews={reviews}
									facility={facility}
									reviewPageNum={reviewPageNum}
									baseUrl={baseUrl}
									source={reviewSource}
									onCreateReview={actions.onCreateReview}
								/>
							</Section>
							{!isEmpty(nearbyFacilities) && (
								<NearbyFacilities
									facilities={nearbyFacilities}
									getFacilityCardPhoneNumber={
										actions.getFacilityCardPhoneNumber
									}
									searchId={facility.nearbyFacilitiesSearchId}
									baseUrl={baseUrl}
								/>
							)}
						</Column>
					</Row>
				</Container>
			</Section>
		</Page>
	);
}

FacilityPage.propTypes = {
	actions: PropTypes.shape({
		changeFormData: PropTypes.func.isRequired,
		getFacilityCardPhoneNumber: PropTypes.func.isRequired,
		createBooking: PropTypes.func.isRequired,
		onCreateReview: PropTypes.func.isRequired,
	}).isRequired,
	amenities: PropTypes.object.isRequired,
	amenitiesShowing: PropTypes.bool,
	baseUrl: PropTypes.string,
	descriptionShowing: PropTypes.bool.isRequired,
	facility: PropTypes.object.isRequired,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	onToggleDescription: PropTypes.func.isRequired,
	onToggleAmenities: PropTypes.func.isRequired,
	phone: phonePropType,
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewPageNum: PropTypes.number,
	reviewSource: PropTypes.string,
	storageUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	vehicleUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	handleSearch: PropTypes.func.isRequired,
	searchLocation: PropTypes.string,
	trackingPixel: PropTypes.string.isRequired,
	scarcityShow: PropTypes.bool,
};

export default enhance(FacilityPage);
